import { useQuery } from '@tanstack/react-query';
import { Users, UserPlus, UserCheck, UserX } from 'lucide-react';
import { StatCard } from '@/components/dashboard/StatCard';
import { UserGrowthChart } from '@/components/dashboard/UserGrowthChart';
import { RecentSignupsTable } from '@/components/dashboard/RecentSignupsTable';
import { apiClient } from '@/services/api-client';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  disabledUsers: number;
  newThisMonth: number;
  recentUsers: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    role: string;
    createdAt: string;
  }[];
}

// Dummy growth data for the chart (will be replaced with real API data later)
function generateDummyGrowthData() {
  const data = [];
  let cumulative = 12;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  for (let i = 0; i < 12; i++) {
    const count = Math.floor(Math.random() * 8) + 2;
    cumulative += count;
    data.push({ date: months[i], count, cumulative });
  }
  return data;
}

const dummyGrowthData = generateDummyGrowthData();

export function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const response = await apiClient.get<DashboardStats>('/api/v1/admin/stats');
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your platform metrics and performance</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={stats?.totalUsers ?? 0}
          change={12.5}
          changeLabel="vs last month"
          icon={Users}
        />
        <StatCard title="Active Users" value={stats?.activeUsers ?? 0} icon={UserCheck} />
        <StatCard
          title="New This Month"
          value={stats?.newThisMonth ?? 0}
          change={8.2}
          changeLabel="vs last month"
          icon={UserPlus}
        />
        <StatCard title="Disabled Users" value={stats?.disabledUsers ?? 0} icon={UserX} />
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <UserGrowthChart data={dummyGrowthData} isLoading={isLoading} />
        <RecentSignupsTable data={stats?.recentUsers ?? []} isLoading={isLoading} />
      </div>
    </div>
  );
}
